import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../../utils/axios";
import Swal from "sweetalert2";
import {
  Shield,
  ShieldCheck,
  UserPlus,
  Search,
  Key,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

interface AdminItem {
  id: number;
  username: string;
  email: string;
  role: string;
  status: string;
  is_active: boolean;
  permissions?: string[] | any;
  resolved_permissions?: string[];
  created_at: string;
}

const AVAILABLE_PERMISSIONS = [
  { id: "USERS_VIEW", label: "View Players / Users", module: "Users" },
  { id: "USERS_EDIT", label: "Edit Players Profile", module: "Users" },
  { id: "USERS_BLOCK", label: "Block / Suspend Players", module: "Users" },
  { id: "USERS_WALLET_UPDATE", label: "Manual Balance Adjust", module: "Users" },

  { id: "AGENT_VIEW", label: "View Agents & Stores", module: "Agents" },
  { id: "AGENT_CREATE", label: "Create Agents", module: "Agents" },
  { id: "AGENT_EDIT", label: "Edit Agents", module: "Agents" },
  { id: "AGENT_DISABLE", label: "Suspend Agents", module: "Agents" },
  { id: "AGENT_REGENERATE_QR", label: "Regenerate Agent QR", module: "Agents" },

  { id: "BACKOFFICE_VIEW", label: "View Back Office Staff", module: "Back Office" },
  { id: "BACKOFFICE_CREATE", label: "Create Back Office Staff", module: "Back Office" },
  { id: "BACKOFFICE_EDIT", label: "Edit Back Office Staff", module: "Back Office" },
  { id: "BACKOFFICE_DISABLE", label: "Suspend Back Office Staff", module: "Back Office" },

  { id: "FINANCE_VIEW", label: "View Financials", module: "Finance" },
  { id: "DEPOSIT_VIEW", label: "View Recharges", module: "Finance" },
  { id: "DEPOSIT_APPROVE_REJECT", label: "Approve/Reject Recharges", module: "Finance" },
  { id: "WITHDRAWAL_VIEW", label: "View Withdrawals", module: "Finance" },
  { id: "WITHDRAWAL_APPROVE_REJECT", label: "Approve/Reject Withdrawals", module: "Finance" },

  { id: "BONUS_VIEW", label: "View Bonuses", module: "Bonuses" },
  { id: "BONUS_CREATE", label: "Issue Bonuses / Penalties", module: "Bonuses" },
  { id: "BONUS_EDIT", label: "Edit Bonus Settings", module: "Bonuses" },

  { id: "REPORT_VIEW", label: "View Bet & System Reports", module: "Reports" },
  { id: "ANALYTICS_VIEW", label: "View Analytics Dashboard", module: "Reports" },

  { id: "SYSTEM_SETTINGS_VIEW", label: "View System Settings", module: "Settings" },
  { id: "SYSTEM_SETTINGS_EDIT", label: "Edit System Settings", module: "Settings" },
  { id: "AUDIT_LOGS_VIEW", label: "View Audit Logs", module: "Audit" },
];

const AdminsManagement: React.FC = () => {
  const { user: currentActor } = useAuth();
  const [admins, setAdmins] = useState<AdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Add Admin Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    email: "",
    password: "",
    permissions: [] as string[],
  });
  const [submitting, setSubmitting] = useState(false);

  // Permissions Modal
  const [isPermsModalOpen, setIsPermsModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminItem | null>(null);
  const [editPermissions, setEditPermissions] = useState<string[]>([]);

  useEffect(() => {
    fetchAdmins();
  }, [page]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admins", {
        params: { page, limit: 15, search },
      });
      if (res.data?.success) {
        setAdmins(res.data.data.admins || []);
        setTotalPages(res.data.data.pagination.totalPages || 1);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchAdmins();
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAdmin.username || !newAdmin.email || !newAdmin.password) {
      toast.error("Please fill in username, email, and password");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/admins", newAdmin);
      if (res.data?.success) {
        toast.success("Administrator created successfully");
        setIsAddModalOpen(false);
        setNewAdmin({ username: "", email: "", password: "", permissions: [] });
        fetchAdmins();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to create administrator");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin: AdminItem) => {
    if (admin.role === "SUPER_ADMIN") {
      toast.warn("Super Admin cannot be disabled");
      return;
    }

    const newStatus = admin.status === "active" ? "disabled" : "active";
    const actionLabel = newStatus === "active" ? "activate" : "disable";

    const confirm = await Swal.fire({
      title: `Are you sure?`,
      text: `Do you want to ${actionLabel} administrator "${admin.username}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "active" ? "#10B981" : "#EF4444",
      confirmButtonText: `Yes, ${actionLabel}`,
    });

    if (confirm.isConfirmed) {
      try {
        const res = await api.patch(`/admins/${admin.id}/status`, { status: newStatus });
        if (res.data?.success) {
          toast.success(res.data.message);
          fetchAdmins();
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || `Failed to ${actionLabel} admin`);
      }
    }
  };

  const handleResetPassword = async (admin: AdminItem) => {
    const { value: password } = await Swal.fire({
      title: `Reset Password for ${admin.username}`,
      input: "password",
      inputLabel: "New Password",
      inputPlaceholder: "Enter new password (min. 6 characters)",
      inputAttributes: {
        minlength: "6",
        autocapitalize: "off",
        autocorrect: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Update Password",
    });

    if (password) {
      try {
        const res = await api.post(`/admins/${admin.id}/reset-password`, { newPassword: password });
        if (res.data?.success) {
          toast.success("Password reset successfully");
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || "Failed to reset password");
      }
    }
  };

  const openPermissionsModal = (admin: AdminItem) => {
    setSelectedAdmin(admin);
    let perms: string[] = [];
    if (Array.isArray(admin.permissions)) {
      perms = admin.permissions;
    } else if (admin.resolved_permissions) {
      perms = admin.resolved_permissions;
    }
    setEditPermissions(perms);
    setIsPermsModalOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedAdmin) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/admins/${selectedAdmin.id}/permissions`, {
        permissions: editPermissions,
      });
      if (res.data?.success) {
        toast.success("Permissions updated successfully");
        setIsPermsModalOpen(false);
        fetchAdmins();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update permissions");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePerm = (permId: string) => {
    setEditPermissions((prev) =>
      prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
    );
  };

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              <Shield size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Administrators</h1>
              <p className="text-sm text-gray-500">
                Manage system administrators, credentials, and module permissions.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-medium transition shadow-sm"
        >
          <UserPlus size={18} />
          <span>Add Administrator</span>
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex justify-between items-center gap-4">
        <form onSubmit={handleSearch} className="flex-1 max-w-md relative">
          <Search size={18} className="absolute left-3.5 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">S.No</th>
                <th className="px-6 py-4">Administrator</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Permissions</th>
                <th className="px-6 py-4">Created Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    <Loader2 className="animate-spin inline-block mr-2" size={20} />
                    Loading administrators...
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    No administrators found.
                  </td>
                </tr>
              ) : (
                admins.map((adm, index) => {
                  const isSuper = adm.role === "SUPER_ADMIN";
                  const isActive = adm.status === "active";
                  return (
                    <tr key={adm.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 font-mono text-xs text-gray-400">
                        #{(page - 1) * 15 + index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs ${isSuper
                              ? "bg-amber-100 text-amber-700"
                              : "bg-indigo-100 text-indigo-700"
                              }`}
                          >
                            {adm.username.substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800 flex items-center gap-1.5">
                              {adm.username}
                              {isSuper && (
                                <span title="Super Admin" className="text-amber-500">
                                  👑
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400">UID: #{adm.id}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-gray-700">{adm.email}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${isSuper
                            ? "bg-amber-50 text-amber-700 border border-amber-200"
                            : "bg-indigo-50 text-indigo-700 border border-indigo-200"
                            }`}
                        >
                          <Shield size={12} />
                          {adm.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-700"
                            }`}
                        >
                          {isActive ? (
                            <CheckCircle size={12} className="text-emerald-500" />
                          ) : (
                            <XCircle size={12} className="text-red-500" />
                          )}
                          {adm.status || "active"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {isSuper ? (
                          <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                            Full Access (*)
                          </span>
                        ) : (
                          <button
                            onClick={() => openPermissionsModal(adm)}
                            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg transition"
                          >
                            {(adm.resolved_permissions || []).length} assigned • Configure
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {new Date(adm.created_at || Date.now()).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleResetPassword(adm)}
                            title="Reset Password"
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                          >
                            <Key size={16} />
                          </button>

                          {!isSuper && (
                            <button
                              onClick={() => handleToggleStatus(adm)}
                              title={isActive ? "Disable Admin" : "Enable Admin"}
                              className={`p-1.5 rounded-lg transition ${isActive
                                ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
                                : "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"
                                }`}
                            >
                              {isActive ? <XCircle size={16} /> : <CheckCircle size={16} />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Admin Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-xl w-full p-6 space-y-5 animate-in fade-in zoom-in-95">
            <div className="flex justify-between items-center border-b pb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-indigo-600" size={22} />
                <h3 className="font-bold text-lg text-gray-900">Create Administrator</h3>
              </div>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Full Name / Username *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. John Doe"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  placeholder="admin@example.com"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  placeholder="Minimum 6 characters"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition flex items-center gap-2"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  Create Administrator
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {isPermsModalOpen && selectedAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-2xl w-full p-6 space-y-5 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center border-b pb-4">
              <div>
                <h3 className="font-bold text-lg text-gray-900">
                  Assign Permissions: {selectedAdmin.username}
                </h3>
                <p className="text-xs text-gray-500">Configure granted module privileges.</p>
              </div>
              <button
                onClick={() => setIsPermsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AVAILABLE_PERMISSIONS.map((perm) => {
                  const isChecked = editPermissions.includes(perm.id);
                  return (
                    <label
                      key={perm.id}
                      onClick={() => togglePerm(perm.id)}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition select-none ${isChecked
                        ? "bg-indigo-50/50 border-indigo-200 text-indigo-900"
                        : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => { }}
                        className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <div className="text-xs font-semibold">{perm.label}</div>
                        <div className="text-[11px] text-gray-400 font-mono">{perm.id}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-xs text-gray-500">
                {editPermissions.length} permission(s) selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setIsPermsModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePermissions}
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition flex items-center gap-2"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  Save Permissions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminsManagement;
